<template>
  <div class="min-h-screen bg-white">
    <Header />

    <main>
      <!-- ═══ HERO ═══ -->
      <section class="relative overflow-hidden bg-white pt-32 pb-16 md:pt-40 md:pb-20">
        <div class="paw-pattern pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true" />
        <div class="relative mx-auto max-w-6xl px-4 text-center md:px-6">
          <h1 class="text-5xl font-bold leading-[1.05] tracking-tight text-foreground md:text-6xl">
            {{ $ts('hero.title') }}
          </h1>
          <p class="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            {{ $ts('hero.subtitle') }}
          </p>
        </div>
      </section>

      <!-- ═══ APP CARDS ═══ -->
      <section class="bg-white py-8 pb-20 md:pb-28">
        <div class="mx-auto max-w-4xl px-4 md:px-6">
          <div class="grid gap-5 md:grid-cols-2">

            <!-- Pet Owners App -->
            <div class="flex flex-col rounded-3xl border border-border/50 bg-white p-7">
              <div class="mb-5 flex items-center gap-4">
                <Icon name="tabler:paw-filled" size="36" class="shrink-0 text-foreground" />
                <div>
                  <h2 class="text-base font-bold text-foreground">Bubba</h2>
                  <p class="text-sm text-muted-foreground">{{ $ts('apps.client.forLabel') }}</p>
                </div>
              </div>
              <p class="mb-6 text-sm leading-relaxed text-muted-foreground">
                {{ $ts('apps.client.description') }}
              </p>
              <div class="mt-auto flex flex-col gap-3">
                <a
                  :href="appStoreLinks.client.ios || undefined"
                  :class="appStoreLinks.client.ios ? '' : 'pointer-events-none opacity-40'"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex h-13 items-center justify-center gap-3 rounded-full bg-foreground px-6 text-background transition-opacity hover:opacity-80"
                >
                  <Icon name="mdi:apple" size="22" />
                  <div class="text-left">
                    <div class="text-[10px] leading-tight opacity-70">Download on the</div>
                    <div class="text-sm font-semibold leading-tight">App Store</div>
                  </div>
                </a>
                <a
                  :href="appStoreLinks.client.android || undefined"
                  :class="appStoreLinks.client.android ? '' : 'pointer-events-none opacity-40'"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex h-13 items-center justify-center gap-3 rounded-full bg-foreground px-6 text-background transition-opacity hover:opacity-80"
                >
                  <Icon name="mdi:google-play" size="22" />
                  <div class="text-left">
                    <div class="text-[10px] leading-tight opacity-70">Get it on</div>
                    <div class="text-sm font-semibold leading-tight">Google Play</div>
                  </div>
                </a>
                <p v-if="!appStoreLinks.client.ios && !appStoreLinks.client.android" class="text-center text-xs text-muted-foreground">
                  {{ $ts('apps.comingSoon') }}
                </p>
              </div>
            </div>

            <!-- Business Partners App -->
            <div class="flex flex-col rounded-3xl border border-border/50 bg-white p-7">
              <div class="mb-5 flex items-center gap-4">
                <Icon name="tabler:briefcase" size="36" class="shrink-0 text-foreground" />
                <div>
                  <h2 class="text-base font-bold text-foreground">Bubba Console</h2>
                  <p class="text-sm text-muted-foreground">{{ $ts('apps.console.forLabel') }}</p>
                </div>
              </div>
              <p class="mb-6 text-sm leading-relaxed text-muted-foreground">
                {{ $ts('apps.console.description') }}
              </p>
              <div class="mt-auto flex flex-col gap-3">
                <a
                  :href="appStoreLinks.console.ios || undefined"
                  :class="appStoreLinks.console.ios ? '' : 'pointer-events-none opacity-40'"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex h-13 items-center justify-center gap-3 rounded-full bg-foreground px-6 text-background transition-opacity hover:opacity-80"
                >
                  <Icon name="mdi:apple" size="22" />
                  <div class="text-left">
                    <div class="text-[10px] leading-tight opacity-70">Download on the</div>
                    <div class="text-sm font-semibold leading-tight">App Store</div>
                  </div>
                </a>
                <a
                  :href="appStoreLinks.console.android || undefined"
                  :class="appStoreLinks.console.android ? '' : 'pointer-events-none opacity-40'"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex h-13 items-center justify-center gap-3 rounded-full bg-foreground px-6 text-background transition-opacity hover:opacity-80"
                >
                  <Icon name="mdi:google-play" size="22" />
                  <div class="text-left">
                    <div class="text-[10px] leading-tight opacity-70">Get it on</div>
                    <div class="text-sm font-semibold leading-tight">Google Play</div>
                  </div>
                </a>
                <p v-if="!appStoreLinks.console.ios && !appStoreLinks.console.android" class="text-center text-xs text-muted-foreground">
                  {{ $ts('apps.comingSoon') }}
                </p>
              </div>
            </div>

          </div>

          <!-- Web note -->
          <p class="mt-8 text-center text-sm text-muted-foreground">
            {{ $ts('webNote.text') }}
            <a href="https://bubba.pet" target="_blank" rel="noopener noreferrer" class="font-semibold text-foreground transition-opacity hover:opacity-70">bubba.pet</a>
          </p>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import { appStoreLinks } from '#bubba/shared/config/links';
import { useI18n, useSeoMeta, useSchemaOrg, defineWebPage } from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';

definePageMeta({ layout: false });

const { $ts } = useI18n();

useSeoMeta({
  title: () => $ts('seo.title'),
  description: () => $ts('seo.description'),
});

useSchemaOrg([defineWebPage({ '@type': 'WebPage' })]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Download' },
]);
</script>
